// Helpers for excluded (outage) days: (market, target day, horizon) triples a
// superuser leaves out of rankings and payments (/market/excluded-days/).
// Charts keep an excluded day on their axis but empty, under this hatch.

export const formatHorizon = (offset) =>
  (Number(offset) === 0 ? 'Intraday' : `D+${offset}`);

// Grey diagonal stripes for excluded days (strip tiles, matrix cells, legend).
export const EXCLUDED_HATCH_CLASS =
  'bg-[repeating-linear-gradient(135deg,rgba(100,116,139,0.30)_0_2px,transparent_2px_6px)] '
  + 'dark:bg-[repeating-linear-gradient(135deg,rgba(148,163,184,0.30)_0_2px,transparent_2px_6px)]';

/**
 * The excluded days of one horizon in one month.
 *
 * @param {Array<{target_day: string, horizon_offset: number, reason: string}>} exclusions
 * @returns {Map<string, string>} ISO day -> reason
 */
export function excludedDaysOf(exclusions, {horizon, year, month}) {
  const prefix = `${year}-${String(month).padStart(2, '0')}-`;
  return new Map((exclusions || [])
    .filter((e) => Number(e.horizon_offset) === Number(horizon)
      && e.target_day.startsWith(prefix))
    .map((e) => [e.target_day, e.reason]));
}

/**
 * A daily leaderboard series with every excluded day present as an empty
 * bucket flagged `excluded: {reason}`, in date order. Other grains pass
 * through untouched.
 */
export function withExcludedBuckets(data, excludedDays) {
  if (!data || data.grain !== 'day' || !excludedDays.size) return data;
  const byKey = new Map((data.buckets || []).map((b) => [b.key, b]));
  excludedDays.forEach((reason, day) => {
    byKey.set(day, {
      key: day, label: String(Number(day.slice(8))), rows: [], excluded: {reason},
    });
  });
  const buckets = [...byKey.values()].sort((a, b) => a.key.localeCompare(b.key));
  return {...data, buckets};
}

/**
 * Daily `{date, value}` entries with every excluded day present, value
 * blanked and `excludedReason` set, in date order.
 */
export function withExcludedEntries(entries, excludedDays) {
  if (!excludedDays.size) return entries;
  const byDate = new Map((entries || []).map((e) => [e.date, e]));
  excludedDays.forEach((reason, day) => {
    byDate.set(day, {date: day, value: null, excludedReason: reason});
  });
  return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
}

// Anchored, fixed-width pattern: no backtracking, so no ReDoS.
// nosemgrep: nodejs_scan.javascript-dos-rule-regex_dos
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Every ISO day from `from` to `to`, both included. Empty when either end is
 * missing or the range is reversed.
 */
export function daysBetween(from, to) {
  if (!ISO_DAY.test(from || '') || !ISO_DAY.test(to || '')) return [];
  const days = [];
  // UTC arithmetic: a local-time loop can repeat or skip a day around DST.
  const last = new Date(`${to}T00:00:00Z`);
  for (let day = new Date(`${from}T00:00:00Z`); day <= last;
    day.setUTCDate(day.getUTCDate() + 1)) {
    days.push(day.toISOString().slice(0, 10));
  }
  return days;
}
